import { useState, useContext, useRef, useEffect, useCallback } from "react";
import TenantContext from "../../../../context/tenant/TenantContext";
import AuthContext from "../../../../context/auth/AuthContext";
import { haveValue, toast } from "../../../../helper/common";
import { trackClarityEvent } from "../../../../Utils/clarityReady";
import constants from "../../../../Utils/constants";
import socket from "../../../../socket";
import {
  getCountriesForDocType,
  getDocumentsForCountry,
} from "../documentSelection";

export const KYC_STEP = {
  START: "start",
  SELECT_DOC: "selectDoc",
  CONTINUE_ON_PHONE: "continueOnPhone",
  CAPTURE_FRONT: "captureFront",
  CAPTURE_BACK: "captureBack",
  UPLOADING: "uploading",
  PROCESSING: "processing",
} as const;

export type KycStep = (typeof KYC_STEP)[keyof typeof KYC_STEP];

interface UseKycWorkflowProps {
  user: { _id: string; [key: string]: any };
  isGuarantor: boolean;
  usedFor: string;
  setShowKycPopup: (show: boolean) => void;
  setAddedFiles: (files: any[]) => void;
  getNextStep: () => number;
  updateUserProfile: (data: any) => void;
}

const MAX_DOCUMENT_RETRIES = 2;

const normalizeImage = (file: File | Blob): Promise<File> => {
  if (!(file instanceof File)) return Promise.resolve(file as File);
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(
              new File([blob], file.name.replace(/\.[^.]+$/, ".jpeg"), {
                type: "image/jpeg",
              })
            );
          } else {
            reject(new Error("Failed to convert image"));
          }
        },
        "image/jpeg",
        0.92
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

const isValidImageFile = (file: File | null | undefined): boolean =>
  file && ["image/jpeg", "image/png", "image/jpg"].includes(file.type);

export const useKycWorkflow = ({
  user,
  isGuarantor,
  usedFor,
  setShowKycPopup,
  setAddedFiles,
  getNextStep,
  updateUserProfile,
}: UseKycWorkflowProps) => {
  const { loadUser, loadUserTenant } = useContext(AuthContext);
  const {
    onfidoKyc,
    initiateCustomEntrustKyc,
    uploadEntrustDocument,
    createEntrustWorkflowRun,
    getEntrustWorkflowStatus,
    createMobileKycSession,
    cancelMobileKycSession,
  } = useContext(TenantContext);

  const [failedKyc, setFailedKyc] = useState(false);
  const [failedWorkflowResult, setFailedWorkflowResult] = useState<Record<
    string,
    any
  > | null>(null);
  const [customKycStarted, setCustomKycStarted] = useState(false);
  const [onfidoInitiateLoading, setOnfidoInitiateLoading] = useState(false);
  const [kycInitiationFailed, setKycInitiationFailed] = useState(false);
  const [kycVerificationPopup, setKycVerificationPopup] = useState(false);
  const [showOnfidoWatingLoading, setShowOnfidoWatingLoading] = useState(false);
  const [showKycLoading, setShowKycLoading] = useState(false);

  const [kycStep, setKycStep] = useState<KycStep>(KYC_STEP.START);
  const [docType, setDocType] = useState("passport");
  const [issuingCountry, setIssuingCountry] = useState("");
  const [frontImage, setFrontImage] = useState<File | Blob | null>(null);
  const [backImage, setBackImage] = useState<File | Blob | null>(null);
  const [applicantId, setApplicantId] = useState("");
  const [captureSubMode, setCaptureSubMode] = useState<string | null>(null);
  const [showDocumentRetryMessage, setShowDocumentRetryMessage] =
    useState(false);
  const documentRetryCount = useRef(0);

  const abortControllerRef = useRef<AbortController | null>(null);

  const [mobileSessionData, setMobileSessionData] = useState<{
    sessionId: string;
    qrUrl: string;
    copyUrl: string;
    expiresAt: string;
  } | null>(null);
  const [mobileSessionState, setMobileSessionState] = useState("PENDING");
  const mobilePollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const clearMobilePolling = useCallback(() => {
    if (mobilePollIntervalRef.current) {
      clearInterval(mobilePollIntervalRef.current);
      mobilePollIntervalRef.current = null;
    }
  }, []);

  const workflowPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const clearWorkflowPolling = useCallback(() => {
    if (workflowPollIntervalRef.current) {
      clearInterval(workflowPollIntervalRef.current);
      workflowPollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const needsBackSide = docType !== "passport";

  useEffect(() => {
    if (!mobileSessionData?.sessionId) return;

    const sessionId = mobileSessionData.sessionId;
    socket.emit("join_kyc_mobile_session", sessionId);

    const onPhoneOpened = () => setMobileSessionState("PHONE_OPENED");
    const onCaptured = (data: any) => {
      if (data?.side === "front") setMobileSessionState("FRONT_CAPTURED");
      else if (data?.side === "back") setMobileSessionState("BACK_CAPTURED");
    };
    const onSubmitted = (data: any) => {
      const wfId = data?.workflow_run_id;
      if (wfId) {
        setMobileSessionState("SUBMITTED");
        handleMobilePollWorkflow(wfId);
      }
    };
    const onWorkflowCompleted = (data: any) => {
      if (
        data?.workflow_status === "approved" ||
        data?.workflow_status === "review"
      ) {
        clearMobilePolling();
        setMobileSessionState("COMPLETED");
        handleKycSuccess();
      } else if (data?.workflow_status === "declined") {
        clearMobilePolling();
        setMobileSessionState("FAILED");
        handleKycFailure(data);
      }
    };
    const onCancelled = () => setMobileSessionState("CANCELLED");

    socket.on("phone_opened", onPhoneOpened);
    socket.on("captured", onCaptured);
    socket.on("submitted", onSubmitted);
    socket.on("workflow_completed", onWorkflowCompleted);
    socket.on("cancelled", onCancelled);

    return () => {
      socket.emit("leave_kyc_mobile_session", sessionId);
      socket.off("phone_opened", onPhoneOpened);
      socket.off("captured", onCaptured);
      socket.off("submitted", onSubmitted);
      socket.off("workflow_completed", onWorkflowCompleted);
      socket.off("cancelled", onCancelled);
      clearMobilePolling();
      clearWorkflowPolling();
    };
    // Reconnecting on every workflow render would register duplicate listeners.
  }, [mobileSessionData?.sessionId, clearMobilePolling, clearWorkflowPolling]);

  const resetKycState = () => {
    setKycStep(KYC_STEP.START);
    setDocType("passport");
    setIssuingCountry("");
    setFrontImage(null);
    setBackImage(null);
    setApplicantId("");
    setCaptureSubMode(null);
    setShowDocumentRetryMessage(false);
    documentRetryCount.current = 0;
    setCustomKycStarted(false);
    setKycInitiationFailed(false);
    setFailedWorkflowResult(null);
    clearMobilePolling();
    clearWorkflowPolling();
    setMobileSessionData(null);
    setMobileSessionState("PENDING");
  };

  const handleCustomKycCancel = () => {
    abortControllerRef.current?.abort();
    resetKycState();
    setOnfidoInitiateLoading(false);
  };

  const handleKycFailure = (workflowResult?: Record<string, any>) => {
    setShowOnfidoWatingLoading(false);
    setShowKycLoading(false);
    resetKycState();
    if (workflowResult) {
      setFailedWorkflowResult(workflowResult);
    }
    setFailedKyc(true);
  };

  const handleDocumentRetry = () => {
    documentRetryCount.current += 1;
    setShowOnfidoWatingLoading(false);
    setShowKycLoading(false);
    setFrontImage(null);
    setBackImage(null);
    setCaptureSubMode(null);
    setAddedFiles([]);
    setShowDocumentRetryMessage(true);
    setKycStep(KYC_STEP.SELECT_DOC);
  };

  const handleKycSuccess = () => {
    setCustomKycStarted(false);
    setKycVerificationPopup(true);
    if (usedFor !== "tenantChecks") {
      loadUserTenant("tenant");
    }

    setTimeout(() => {
      if (usedFor === "popup") {
        setShowKycPopup(false);
      } else if (usedFor === "tenantChecks") {
        const _step = getNextStep();
        updateUserProfile({ id: user?._id, step: _step });
      } else {
        updateUserProfile({ step: 6, role: "tenant" });
        if (!isGuarantor) {
          trackClarityEvent("tenant_kyc_step_ended");
          setTimeout(() => {
            trackClarityEvent("tenant_signup_completed");
            trackClarityEvent("tenant_bank_connect_step_started");
          }, constants.CLARITY_DELAY);
        }
      }
    }, 2000);
  };

  const saveOnfidoKycResponse = (kycData: Record<string, any>) => {
    return onfidoKyc(
      user?._id,
      kycData,
      isGuarantor
        ? "?guarantor=true"
        : usedFor === "tenantChecks"
          ? "?llCreated=true"
          : ""
    );
  };

  const handleWorkflowPoll = (workflowRunId: string) => {
    const MAX_POLLS = 2;
    let attempts = 0;
    clearWorkflowPolling();
    workflowPollIntervalRef.current = setInterval(async () => {
      try {
        attempts += 1;
        const status = await getEntrustWorkflowStatus(workflowRunId);
        if (
          status?.workflow_status === "approved" ||
          status?.workflow_status === "review"
        ) {
          clearWorkflowPolling();
          handleKycSuccess();
        } else if (
          status?.workflow_status === "declined" ||
          status?.workflow_status === "error" ||
          status?.workflow_status === "abandoned"
        ) {
          clearWorkflowPolling();
          const shouldRetryDocuments = haveValue(
            status?.workflow_run?.output?.retry
          );
          if (
            shouldRetryDocuments &&
            documentRetryCount.current < MAX_DOCUMENT_RETRIES
          ) {
            handleDocumentRetry();
          } else {
            handleKycFailure(status);
          }
        } else if (attempts >= MAX_POLLS) {
          clearWorkflowPolling();
          handleKycSuccess();
        }
      } catch (error) {
        console.error("Polling workflow status error:", error);
        if (attempts >= MAX_POLLS) {
          clearWorkflowPolling();
          handleKycSuccess();
        }
      }
    }, 8000);
  };

  const performUpload = async (
    frontImg: File | Blob,
    backImg: File | Blob | null
  ) => {
    setKycStep(KYC_STEP.UPLOADING);

    const abortController = new AbortController();
    abortControllerRef.current?.abort();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    try {
      const uploadImage = async (
        imageBlob: File | Blob,
        fieldName: string,
        type: string,
        side: string
      ): Promise<FormData> => {
        const normalized =
          imageBlob instanceof File
            ? await normalizeImage(imageBlob)
            : imageBlob;
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        const formData = new FormData();
        const fileName = `${fieldName}_${Date.now()}.jpeg`;
        const file = new File([normalized], fileName, {
          type: "image/jpeg",
        });
        formData.append("file", file);
        formData.append("applicant_id", applicantId);
        if (type) formData.append("type", type);
        if (side) formData.append("side", side);
        if (issuingCountry) formData.append("issuing_country", issuingCountry);
        return formData;
      };

      const frontFormData = await uploadImage(
        frontImg,
        "front",
        docType,
        "front"
      );
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const frontResult = await uploadEntrustDocument(frontFormData);
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const frontDocumentId = frontResult.document_id || frontResult.id;
      const documentIds = [frontDocumentId];
      const documentSides: Record<string, { id: string; type: string }> = {
        front: {
          id: frontDocumentId,
          type: "document_photo",
        },
      };

      if (backImg) {
        const backFormData = await uploadImage(
          backImg,
          "back",
          docType,
          "back"
        );
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        const backResult = await uploadEntrustDocument(backFormData);
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        const backDocumentId = backResult.document_id || backResult.id;
        documentIds.push(backDocumentId);
        documentSides.back = {
          id: backDocumentId,
          type: "document_photo",
        };
      }

      if (signal.aborted) throw new DOMException("Aborted", "AbortError");

      const onfidoKycData = {
        welcome: {
          type: "finish",
          output: {},
        },
        upload_document_photo_cb092: {
          type: "finish",
          output: {
            documentType: docType,
            sides: documentSides,
            issuingCountry,
          },
        },
      };

      saveOnfidoKycResponse(onfidoKycData).catch((error) => {
        console.error("Error saving uploaded KYC document data:", error);
      });

      const isLlCreated = usedFor === "tenantChecks";
      const workflowData: Record<string, any> = {
        applicant_id: applicantId,
        document_ids: documentIds,
        is_guarantor: isGuarantor,
        ll_created: isLlCreated,
        doc_type: docType,
        issuing_country: issuingCountry,
      };
      if (isGuarantor || isLlCreated) {
        workflowData.tenant_id = user._id;
      }
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const workflowResult = await createEntrustWorkflowRun(workflowData);
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const wfRunId =
        workflowResult.workflow_run_id ||
        workflowResult.id ||
        workflowResult.workflow_data?.id;

      setShowOnfidoWatingLoading(true);
      setKycStep(KYC_STEP.PROCESSING);

      console.log("workflow starting with wfRunId", wfRunId);
      handleWorkflowPoll(wfRunId);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      console.error("Error during KYC upload:", err);
      toast.error("kycUploadFailed");
      setKycStep(KYC_STEP.SELECT_DOC);
    }
  };

  const startCustomKyc = async () => {
    setAddedFiles([]);
    documentRetryCount.current = 0;
    setShowDocumentRetryMessage(false);
    setKycInitiationFailed(false);
    setOnfidoInitiateLoading(true);

    try {
      const isLlCreated = usedFor === "tenantChecks";
      const result = await initiateCustomEntrustKyc(
        user?._id,
        isGuarantor,
        isLlCreated
      );
      setApplicantId(result.applicant_id);
      setKycStep(KYC_STEP.SELECT_DOC);
      setCustomKycStarted(true);
      setOnfidoInitiateLoading(false);
    } catch (err) {
      console.error("Error initiating custom KYC:", err);
      setOnfidoInitiateLoading(false);
      setKycInitiationFailed(true);
      toast.error("kycInitiationFailed");
    }
  };

  const handleDocTypeSelect = (selectedDocType?: string) => {
    if (selectedDocType) {
      setDocType(selectedDocType);
    }
    setCaptureSubMode(null);
    setKycStep(KYC_STEP.CAPTURE_FRONT);
  };

  const handleStartVerification = () => {
    setCaptureSubMode(null);
    setKycStep(KYC_STEP.SELECT_DOC);
  };

  const handleCreateMobileSession = async () => {
    setKycStep(KYC_STEP.CONTINUE_ON_PHONE);
    try {
      const data = await createMobileKycSession(docType, issuingCountry);
      const sessionUrl = `${window.location.origin}/m/capture?token=${data.sessionId}`;
      setMobileSessionData({
        sessionId: data.sessionId,
        qrUrl: sessionUrl,
        copyUrl: sessionUrl,
        expiresAt: data.expiresAt,
      });
      setMobileSessionState("PENDING");
    } catch (err) {
      console.error("Failed to create mobile session:", err);
      toast.error("mobileSessionCreateFailed");
      setKycStep(KYC_STEP.SELECT_DOC);
    }
  };

  const handleMobilePollWorkflow = (wfRunId: string) => {
    const MAX_POLLS = 60;
    let attempts = 0;
    clearMobilePolling();
    mobilePollIntervalRef.current = setInterval(async () => {
      try {
        attempts += 1;
        const result = await getEntrustWorkflowStatus(wfRunId);
        if (
          result?.workflow_status === "approved" ||
          result?.workflow_status === "review"
        ) {
          clearMobilePolling();
          setMobileSessionState("COMPLETED");
          handleKycSuccess();
        } else if (
          result?.workflow_status === "declined" ||
          result?.workflow_status === "error" ||
          result?.workflow_status === "abandoned"
        ) {
          clearMobilePolling();
          setMobileSessionState("FAILED");
          handleKycFailure(result);
        } else if (attempts >= MAX_POLLS) {
          clearMobilePolling();
          handleKycFailure();
        }
      } catch {
        if (attempts >= MAX_POLLS) {
          clearMobilePolling();
        }
      }
    }, 8000);
  };

  const handleMobileCancel = async () => {
    const sessionId = mobileSessionData?.sessionId;
    clearMobilePolling();
    setKycStep(KYC_STEP.START);
    setMobileSessionData(null);
    setMobileSessionState("PENDING");

    if (sessionId) {
      try {
        await cancelMobileKycSession(sessionId);
      } catch (err) {
        console.error("Failed to cancel mobile KYC session:", err);
      }
    }
  };

  const handleDocTypeChange = (newDocType: string) => {
    setDocType(newDocType);
    const validCountries = getCountriesForDocType(newDocType);
    if (
      issuingCountry &&
      !validCountries.find((c) => c.value === issuingCountry)
    ) {
      setIssuingCountry("");
    }
  };

  const handleCountryChange = (newCountry: string) => {
    setIssuingCountry(newCountry);
    const validDocs = getDocumentsForCountry(newCountry);
    if (!validDocs.find((d) => d.value === docType)) {
      setDocType(validDocs[0]?.value || "passport");
    }
  };

  const handleFrontCapture = (imageBlob: File | Blob) => {
    setFrontImage(imageBlob);
    setCaptureSubMode(null);
    if (needsBackSide) {
      setKycStep(KYC_STEP.CAPTURE_BACK);
    } else {
      performUpload(imageBlob, null);
    }
  };

  const handleFileUploadFront = async (file: File | Blob) => {
    if (file instanceof File && !isValidImageFile(file)) {
      toast.error("onlyJpgAndPngFilesAreAllowed");
      return;
    }
    try {
      const normalized = await normalizeImage(file);
      setFrontImage(normalized);
      setCaptureSubMode(null);
      if (needsBackSide) {
        setKycStep(KYC_STEP.CAPTURE_BACK);
      } else {
        performUpload(normalized, null);
      }
    } catch {
      toast.error("onlyJpgAndPngFilesAreAllowed");
    }
  };

  const handleBackCapture = (imageBlob: File | Blob) => {
    setBackImage(imageBlob);
    setCaptureSubMode(null);
    performUpload(frontImage!, imageBlob);
  };

  const handleFileUploadBack = async (file: File | Blob) => {
    if (file instanceof File && !isValidImageFile(file)) {
      toast.error("onlyJpgAndPngFilesAreAllowed");
      return;
    }
    try {
      const normalized = await normalizeImage(file);
      setBackImage(normalized);
      setCaptureSubMode(null);
      performUpload(frontImage!, normalized);
    } catch {
      toast.error("onlyJpgAndPngFilesAreAllowed");
    }
  };

  return {
    failedKyc,
    setFailedKyc,
    failedWorkflowResult,
    customKycStarted,
    onfidoInitiateLoading,
    kycInitiationFailed,
    kycVerificationPopup,
    setKycVerificationPopup,
    showOnfidoWatingLoading,
    showKycLoading,
    showDocumentRetryMessage,
    setShowKycLoading,
    kycStep,
    docType,
    issuingCountry,
    frontImage,
    setFrontImage,
    backImage,
    setBackImage,
    captureSubMode,
    setCaptureSubMode,
    needsBackSide,
    startCustomKyc,
    handleStartVerification,
    handleCreateMobileSession,
    handleCustomKycCancel,
    handleDocTypeChange,
    handleCountryChange,
    handleDocTypeSelect,
    handleFileUploadFront,
    handleFileUploadBack,
    handleFrontCapture,
    handleBackCapture,
    handleMobilePollWorkflow,
    handleMobileCancel,
    mobileSessionData,
    mobileSessionState,
    setMobileSessionState,
    loadUser,
    setKycStep,
  };
};
